<template>
  <div class="completed-panel">
    <div id="completed-buttons" class="completed-panel__main">
      <div class="completed-panel__hero">
        <div class="completed-panel__hero-head">
          <!-- 装飾なので alt は空。テキストを画面中央に保つため緑背景の外へ逃がす -->
          <img
            class="completed-panel__hero-goat"
            src="/static/renew3_img/completed/yagiphone.png"
            alt=""
            aria-hidden="true"
          />
          <p class="completed-panel__hero-title">
            {{ documentLabel }}が完成しました！
          </p>
          <p class="completed-panel__hero-lead">お疲れさまでした！</p>
          <!-- 折り返しは読点で起きるよう inline-block で塊にする -->
          <p class="completed-panel__hero-text">
            <span>作成した{{ documentLabel }}は、</span
            ><span>以下の方法ですぐに利用できます。</span>
          </p>
        </div>

        <p v-if="!isAuthenticated" class="completed-panel__hero-note">
          ※メール送信・コンビニ印刷・ダウンロードのご利用にはログインが必要です
        </p>

        <!-- auto_rt の同意はメール送信の直前に置く（押す前に読ませる） -->
        <CompletedAutoRtConsent
          v-if="isAutoRtConsentVisible"
          class="completed-panel__hero-consent"
          :info="autoRtInfo"
          :has-partner-terms="hasAutoRtPartnerTerms"
          :partner-terms-html="autoRtPartnerTermsHtml"
          :agreed="isAgree"
          @update:agreed="onRtAgreeChange"
        />

        <div
          :id="isEmailVerified ? 'pdf-email' : 'pdf-email-none'"
          class="completed-panel__hero-primary"
        >
          <CompletedActionCard
            :button-id="isEmailVerified ? pdfMailBtnId : 'pdf-mail-btn'"
            icon-src="/static/renew3_img/completed/icon_mail.svg"
            :title="mailAction.title"
            :note="mailAction.note"
            badge="おすすめ"
            emphasis
            :cta-label="mailAction.cta"
            :disabled="!isOnline || !mailPdfEnabled"
            @click="onMailClick"
          />
        </div>
      </div>

      <div class="completed-panel__actions">
        <p class="completed-panel__others">そのほかの方法を利用する</p>

        <div v-if="eprintEnabled" id="combie" ref="eprintCtaEl">
          <CompletedActionCard
            button-id="pdf-eprint-btn"
            icon-src="/static/renew3_img/completed/icon_eprint.svg"
            title="コンビニで印刷"
            note="全国のコンビニで印刷可能"
            @click="handleEventWithLoginCheck('show-preview-4conv')"
          />
        </div>

        <div v-if="isQuickEmailVisible" id="quickemail">
          <CompletedActionCard
            button-id="quick-email-btn"
            icon-src="/static/renew3_img/completed/icon_quickemail.svg"
            title="応募先にメール"
            :note="quickEmailLabel"
            :disabled="!isOnline || !localIsSendYagiEmailEnabled"
            @click="handleYagiEmailClick"
          />
        </div>

        <div v-if="isPdfDownloadButtonVisible" id="pdf-download">
          <CompletedActionCard
            :button-id="pdfDownloadBtnId"
            icon-src="/static/renew3_img/completed/icon_dl.svg"
            title="PDFをダウンロード"
            note="端末に保存"
            :disabled="!isOnline || !downloadPdfEnabled"
            @click="handleEventWithLoginCheck('download-PDF')"
          />
        </div>

        <!-- 送付状は郵送が前提なので、封筒の宛名ラベルへつなぐ -->
        <AtenaLabelSuggest
          v-if="formatId === 'letter'"
          link-id="atena-suggest-completed-letter"
          frm="completed_letter"
          lead="送付状と一緒に郵送するなら、宛名もコンビニで印刷できます"
        />
      </div>
    </div>

    <div class="completed-panel__help">
      <p class="completed-panel__help-title">
        お困りの場合は以下をご参照ください
      </p>
      <a
        href="https://www.yagish.jp/information/pdf_mail_setting/"
        target="_blank"
        rel="nofollow"
        >※メールが受信できない場合</a
      >
      <a
        v-if="isPdfDownloadButtonVisible"
        href="https://www.yagish.jp/information/2022-04-06-163828/?utm_source=blogWidget"
        target="_blank"
        rel="nofollow"
        >※端末に保存できない場合</a
      >
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 完了画面の出力パネル（旧 DownloadItem.vue の置き換え）。
 * GTM のトリガーが見ている id（completed-buttons / pdf-email / pdf-email-none / combie /
 * quickemail / pdf-download / 各ボタン id）は旧実装から変えていない。
 */
import { computed, ref, toRef } from 'vue'
import CompletedActionCard from './CompletedActionCard.vue'
import CompletedAutoRtConsent from './CompletedAutoRtConsent.vue'
import AtenaLabelSuggest from '@/components/format-edit/AtenaLabelSuggest.vue'
import { useCompletedOutput } from '@/composables/format-edit/useCompletedOutput'
import { resolveCompletedDocumentLabel } from '@/utils/format-edit/completedDocumentLabel'

const props = withDefaults(
  defineProps<{
    formatId?: string
    isSendYagiEmailEnabled?: boolean
    mailPdfEnabled?: boolean
    downloadPdfEnabled?: boolean
    /** コンビニ印刷CTAを出すか。宛名L判など通常の eprint フロー非対応の用紙で false */
    eprintEnabled?: boolean
  }>(),
  {
    formatId: '',
    isSendYagiEmailEnabled: true,
    mailPdfEnabled: true,
    downloadPdfEnabled: true,
    eprintEnabled: true
  }
)

const emit = defineEmits<{
  (e: 'update:isSendYagiEmailEnabled', value: boolean): void
}>()

const eprintCtaEl = ref<HTMLElement | null>(null)

const {
  isAuthenticated,
  isEmailVerified,
  isOnline,
  isPdfDownloadButtonVisible,
  isQuickEmailVisible,
  quickEmailLabel,
  localIsSendYagiEmailEnabled,
  pdfMailBtnId,
  pdfDownloadBtnId,
  autoRtInfo,
  hasAutoRtPartnerTerms,
  autoRtPartnerTermsHtml,
  isAgree,
  handleEventWithLoginCheck,
  handleYagiEmailClick,
  requestEmailVerification,
  observeEprintCtaImpression,
  onRtAgreeChange
} = useCompletedOutput({
  formatId: toRef(props, 'formatId'),
  isSendYagiEmailEnabled: toRef(props, 'isSendYagiEmailEnabled'),
  onSendYagiEmail: () => emit('update:isSendYagiEmailEnabled', false)
})

observeEprintCtaImpression(eprintCtaEl)

const documentLabel = computed(() =>
  resolveCompletedDocumentLabel(props.formatId)
)

const isAutoRtConsentVisible = computed(
  () => isAuthenticated.value && !!autoRtInfo.value.clientId
)

const mailAction = computed(() => {
  if (isEmailVerified.value) {
    return {
      title: 'PDFをメールで送る',
      note: '自分のメールアドレスにPDFデータを送信',
      cta: '送信する'
    }
  }
  if (isAuthenticated.value) {
    return {
      title: '確認メールを送る',
      note: 'メールアドレスの確認後にメールで送信できます',
      cta: '送信する'
    }
  }
  return {
    title: 'PDFをメールで送る',
    note: '自分のメールアドレスにPDFデータを送信',
    cta: 'ログイン'
  }
})

function onMailClick() {
  if (isEmailVerified.value) {
    handleEventWithLoginCheck('mail-PDF')
    return
  }
  if (isAuthenticated.value) {
    requestEmailVerification()
    return
  }
  handleEventWithLoginCheck('send-email-verification')
}
</script>

<style lang="scss" scoped>
$primary: #e3165c;
$deep-green: #004d40;
$hero-bg: #e8f4e6;
$note-color: #6b6b6b;

/* ヤギ（装飾）の表示幅。360px 未満は中央寄せの本文と当たるため一段小さくする */
$goat-width-xs: 48px;
$goat-width-sp: 64px;
$goat-width-pc: 132px;

/*
  ヒーロー右端からのはみ出し量。本文は画面中央寄せのままにしたいので、
  ヤギ側を緑背景の外へ逃がして重なりを避ける。画面外へ出すと横スクロールが
  出るので、ヒーローの左右余白に収まる範囲に留める。
*/
$goat-overhang-sp: 20px;
$goat-overhang-pc: 32px;

.completed-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__main {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__hero {
    padding: 16px 14px;
    border-radius: 12px;
    background: $hero-bg;
    color: $deep-green;
  }

  /** テキストはブロック幅の中央。ヤギは右下に絶対配置（装飾） */
  &__hero-head {
    position: relative;
    padding-bottom: 6px;
    text-align: center;
  }

  &__hero-goat {
    position: absolute;
    right: -$goat-overhang-sp;
    bottom: 0;
    width: $goat-width-sp;
    height: auto;
    pointer-events: none;

    @media (width < 360px) {
      width: $goat-width-xs;
    }
  }

  &__hero-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: bold;
    line-height: 1.3;
  }

  &__hero-lead {
    margin: 2px 0 0;
    font-size: 0.875rem;
    font-weight: bold;
  }

  &__hero-text {
    margin: 8px 0 0;
    font-size: 0.813rem;
    line-height: 1.5;

    /* SP は読点で必ず改行する。1 行に収まる幅だとヤギに突っ込むため */
    span {
      display: block;
    }
  }

  &__hero-note {
    margin: 8px 0 0;
    font-size: 0.688rem;
    line-height: 1.4;
    color: $note-color;
    text-align: center;
  }

  &__hero-consent {
    margin-top: 12px;
  }

  &__hero-primary {
    margin-top: 12px;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__others {
    margin: 0;
    font-size: 0.813rem;
    color: $deep-green;
  }

  @media (width >= 768px) {
    &__hero {
      padding: 20px 24px;
    }

    &__hero-head {
      padding-bottom: 8px;
    }

    &__hero-goat {
      right: -$goat-overhang-pc;
      width: $goat-width-pc;
    }

    &__hero-title {
      font-size: 1.75rem;
    }

    &__hero-lead {
      font-size: 1.25rem;
    }

    &__hero-text {
      font-size: 1rem;

      /* PC は横幅に余裕があるので 1 行に戻す */
      span {
        display: inline-block;
      }
    }

    &__hero-primary {
      margin-top: 20px;
    }
  }

  &__help {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    font-size: 0.813rem;

    a {
      color: $deep-green;
      text-decoration: underline;
    }
  }

  &__help-title {
    margin: 0;
    font-weight: bold;
    color: $deep-green;
  }
}
</style>
